import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import { toast } from "@/shared/hooks/use-toast";
import { errorToast } from "@/shared/lib/apiErrors";
import { clearToken } from "@/shared/api";
import { LOGIN_NOTICE_KEY } from "@/shared/lib/passwordChangeRequired";
import { profileService } from "@/entities/profile";
import { useAuth } from "../model/AuthContext";
import { makeForcePasswordSchemaAskOld } from "./forcePasswordSchema";

type Values = z.infer<ReturnType<typeof makeForcePasswordSchemaAskOld>>;

export const PASSWORD_UPDATED_NOTICE = "密码已更新,请用新密码重新登录";

/**
 * 强制改密闸(0930 修复:首登强制改密「刷新即绕过」)。
 *
 * 会话处于「必须先改密」(AuthContext.passwordChangeRequired)时,ProtectedRoute 用本组件**替换**整站内容:
 * 不可关闭,出口只有「改完密码」或「退出登录」。登录时输入的初始密码刷新后已不在内存,所以这里让用户再输一遍当前密码。
 *
 * 改密成功后端会重签 sid(当前 token 被踢),所以先清 token 再登出(不带死 token 去调 /auth/logout,
 * 否则 401 SESSION_KICKED 会被当成「被踢下线」),回登录页用新密码重登。
 */
export function PasswordChangeGate() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<Values>({
    resolver: zodResolver(makeForcePasswordSchemaAskOld()),
    defaultValues: { oldPassword: "", newPassword: "", confirm: "" },
  });

  const backToLogin = (notice?: string) => {
    clearToken();
    logout();
    try {
      if (notice) sessionStorage.setItem(LOGIN_NOTICE_KEY, notice);
    } catch {
      /* sessionStorage 不可用时不阻断 */
    }
    navigate("/login", { replace: true });
  };

  const submit = async (values: Values) => {
    setSubmitting(true);
    try {
      await profileService.changePassword(values.oldPassword, values.newPassword);
      toast({ title: "密码已更新", description: "请用新密码重新登录。" });
      backToLogin(PASSWORD_UPDATED_NOTICE);
    } catch (err) {
      toast(errorToast(err, { fallbackTitle: "修改失败" }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="password-change-gate">
      <Dialog open>
        {/* 不给 onOpenChange:Esc / 点遮罩都不关 —— 改完密码或退出登录是唯一出口 */}
        <DialogContent className="max-w-sm [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-warning" />
              请先设置新密码
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            当前密码是管理员发给你的初始密码,转交过程中可能被别人看到。设置一个新密码后才能使用系统。
          </p>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
              <FormField
                control={form.control}
                name="oldPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>当前密码</FormLabel>
                    <FormControl>
                      <Input type="password" autoFocus autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>新密码</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>再输一遍</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="flex-col gap-2 sm:flex-col">
                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  保存新密码
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  disabled={submitting}
                  onClick={() => backToLogin()}
                >
                  退出登录
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
